import React from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { getDealerFeeData, getDealerFeeAmount, getDealerFeeType } from '|/helpers/dealer-fees';
import styles from './no-dealer-fees-modal.module.css';

export interface NoDealerFeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userState?: string;
}

const NoDealerFeesModal: React.FC<NoDealerFeesModalProps> = ({ isOpen, onClose, userState }) => {
  const stateData = userState ? getDealerFeeData(userState) : null;
  const feeType = stateData ? getDealerFeeType(stateData) : null;

  return (
    <EventedModal
      analyticsId={ModalNames.NoDealerFees}
      isOpen={isOpen}
      onClose={onClose}
      mobileStyle="inset"
      desktopWidth="480px"
      contentPadding={false}
      divLine={false}
    >
      <div className={styles.container} data-testid="no-dealer-fees-modal">
        <div className={styles.content}>
          <div className={styles.section}>
            <h2 className={styles.mainTitle}>
              At Carvana, you&apos;ll never pay hidden dealer fees.
            </h2>
            <p className={styles.text}>
              We have a transparent, haggle-free experience designed to give you the lowest price
              possible.
            </p>
          </div>

          <div className={styles.feesDetailsSection}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>What are hidden dealer fees?</h3>
              <p className={styles.text}>
                Other dealers may add extra fees, such as &quot;doc fees,&quot; or include add-on
                products in your total price. These are typically non-negotiable and add costs above
                the required states taxes and fees. In some states, these fees can reach $995.
              </p>
            </div>

            {stateData && (
              <div className={styles.section}>
                <h3 className={styles.stateTitle}>Hidden dealer fees in {stateData.state}:</h3>
                <ul className={styles.stateList}>
                  <li className={styles.stateItem}>
                    The{' '}
                    <span className={styles.stateLabel}>
                      {feeType} doc fee is ${getDealerFeeAmount(stateData)}
                    </span>
                  </li>
                  <li className={styles.stateItem}>
                    Dealers can also include add-ons like window tinting and paint protection at
                    high costs
                  </li>
                </ul>
                <p className={styles.text}>
                  These numbers are rarely displayed in the sticker price. Keep this in mind when
                  comparing prices.
                </p>
              </div>
            )}
          </div>

          <p className={styles.disclaimer}>
            We gather information from external sources to show the average doc fee charged in your
            state by other dealerships. The maximum doc fees are based on regulations in your state.
            While we do our best to keep this information as accurate as possible, we encourage you
            to do your own research on fees, charges and add-ons when vehicle shopping.
          </p>
        </div>
      </div>
    </EventedModal>
  );
};

export default NoDealerFeesModal;
